import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  removeFunctionSpecsUsingEdge,
  removeExpressionsFromRouterConfig,
} from 'processes/graph-builder-processes/flowchart-viz/action-functions/deleteEdgeProcessFlowchartActionFunction';
import {findAllUpstreamMappableNodeIds} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {
  CreateOrUpdateRouterFunctionSpec,
  RouterConfig,
  UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC,
  UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
} from 'processes/modals/router-config/RouterConfigConstants';

import {StaticTemplateFlowchart} from '../processFlowchartConfigEditorConstants';
import {isStaticRouterNodeData, RouterStaticNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartEdgeChange,
  FlowchartEdgeChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
} from '../processFlowchartConfigEditorUtils';

function getEdgesRouterFunctionsDifference(
  routerConfig: RouterConfig,
  numRouterNodeOutboundEdges: number
): number {
  const numExistingFunctionSpecs =
    routerConfig.functionSpecs.length + (routerConfig.defaultFunctionSpec != null ? 1 : 0);
  const numEdgesWithoutFunctionSpecs = numRouterNodeOutboundEdges - numExistingFunctionSpecs;

  return numEdgesWithoutFunctionSpecs;
}

function addUnconfiguredFunctionsForRouterConfig(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number,
  numRouterOutboundEdges: number
): RouterConfig {
  const newFunctionSpecs: Array<CreateOrUpdateRouterFunctionSpec> = [];
  const numNewFunctionSpecsNeeded =
    routerConfig.defaultFunctionSpec == null
      ? numEdgesWithoutFunctionSpecs - 1
      : numEdgesWithoutFunctionSpecs;

  let newFunctionSpecCreated = 0;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (defaultFunctionSpec == null) {
    defaultFunctionSpec = {...UNCONFIGURED_DEFAULT_ROUTER_FUNCTION_SPEC, clientId: uuidv4()};
    newFunctionSpecCreated++;
  }
  while (newFunctionSpecCreated < numNewFunctionSpecsNeeded) {
    newFunctionSpecs.push({
      ...UNCONFIGURED_NON_DEFAULT_ROUTER_FUNCTION_SPEC,
      name: `Route ${numRouterOutboundEdges - numNewFunctionSpecsNeeded + newFunctionSpecCreated}`,
      clientId: uuidv4(),
    });
    newFunctionSpecCreated++;
  }
  return {
    ...routerConfig,
    functionSpecs: routerConfig.functionSpecs.concat(newFunctionSpecs),
    defaultFunctionSpec,
  };
}

function removeUnusedFunctionSpecs(
  routerConfig: RouterConfig,
  numEdgesWithoutFunctionSpecs: number,
  staticFlowchart: StaticTemplateFlowchart
): RouterConfig {
  const existingEdgeIds = new Set(staticFlowchart.edgesStaticData.map((edge) => edge.id));

  const isFunctionSpecRemovable = (functionSpec: CreateOrUpdateRouterFunctionSpec): boolean => {
    // Function spec is removable if it has no edge reference (unconfigured)
    if (functionSpec.edgeConfigReference == null) {
      return true;
    }
    // Function spec is also removable if its edge reference points to an edge that no longer exists
    return !existingEdgeIds.has(functionSpec.edgeConfigReference.clientId);
  };

  assert(numEdgesWithoutFunctionSpecs > 0, 'Expected numEdgesWithoutFunctionSpecs to be positive');
  const newFunctionSpecs = [];
  let functionSpecsRemoved = 0;
  for (const functionSpec of routerConfig.functionSpecs) {
    if (
      !isFunctionSpecRemovable(functionSpec) ||
      functionSpecsRemoved >= Math.abs(numEdgesWithoutFunctionSpecs)
    ) {
      newFunctionSpecs.push(functionSpec);
    } else {
      functionSpecsRemoved++;
    }
  }

  const numSpecsStillToRemove = numEdgesWithoutFunctionSpecs - functionSpecsRemoved;
  let defaultFunctionSpec = routerConfig.defaultFunctionSpec;
  if (
    numSpecsStillToRemove > 0 &&
    defaultFunctionSpec != null &&
    isFunctionSpecRemovable(defaultFunctionSpec)
  ) {
    defaultFunctionSpec = null;
    functionSpecsRemoved++;
  }

  assert(
    functionSpecsRemoved === numEdgesWithoutFunctionSpecs,
    'Did not remove expected number of function specs',
    {
      functionSpecsRemoved,
      numEdgesWithoutFunctionSpecs,
      defaultFunctionSpec,
    }
  );

  routerConfig = {
    ...routerConfig,
    functionSpecs: newFunctionSpecs,
    defaultFunctionSpec,
  };
  return routerConfig;
}

/**
 * Checks if a router node has an incoming edge from a task node, or an incoming edge
 * from a router chain that ultimately has an incoming edge from a task node.
 *
 * @param staticFlowchart the flowchart containing the router node.
 * @param routerNode the router node to check.
 * @returns true if the router node has an incoming edge from a task node or connected router chain, false otherwise.
 */
export function doesRouterHaveMappableSourceNode(
  staticFlowchart: StaticTemplateFlowchart,
  routerNode: RouterStaticNodeData
): boolean {
  return (
    findAllUpstreamMappableNodeIds(
      {
        nodes: staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
        edges: staticFlowchart.edgesStaticData,
      },
      routerNode.id
    ).length > 0
  );
}

export interface RouterConfigChangesAfterEdgeChange {
  nodeChanges: Array<FlowchartNodeChange>;
  edgeChanges: Array<FlowchartEdgeChange>;
}

/**
 * Adding/removing an edge requires recalculating function specs for router nodes.
 * On removal, we also need to emit edge label changes to clear labels on remaining outbound edges
 * that are no longer referenced by any function spec (e.g., deleting the "all other cases"
 * edge clears all specs, so remaining edges' labels become stale).
 */
export default function getRouterConfigChangesAfterEdgeChange(
  staticFlowchart: StaticTemplateFlowchart,
  changeType: FlowchartEdgeChangeType.ADD | FlowchartEdgeChangeType.REMOVE,
  edgeId?: string
): RouterConfigChangesAfterEdgeChange {
  // Only process if we have router nodes
  const routerNodes = staticFlowchart.nodesStaticData.filter((node): node is RouterStaticNodeData =>
    isStaticRouterNodeData(node)
  );

  if (routerNodes.length === 0) {
    return {nodeChanges: [], edgeChanges: []};
  }

  const nodeChanges: Array<FlowchartNodeChange> = [];
  const edgeChanges: Array<FlowchartEdgeChange> = [];

  for (const routerNode of routerNodes) {
    let updatedRouterConfig = {...routerNode.routerConfig};

    // Handle edge removal
    if (changeType === FlowchartEdgeChangeType.REMOVE && edgeId != null) {
      if (!doesRouterHaveMappableSourceNode(staticFlowchart, routerNode)) {
        updatedRouterConfig = removeExpressionsFromRouterConfig(updatedRouterConfig);
      }

      updatedRouterConfig = removeFunctionSpecsUsingEdge(edgeId, updatedRouterConfig);
    }

    // Recalculate function specs based on current edges
    const outboundEdges = staticFlowchart.edgesStaticData.filter((edge) => edge.source === routerNode.id);
    const numOutboundEdges = outboundEdges.length;
    const numEdgesWithoutFunctionSpecs = getEdgesRouterFunctionsDifference(
      updatedRouterConfig,
      numOutboundEdges
    );

    if (numEdgesWithoutFunctionSpecs > 0) {
      updatedRouterConfig = addUnconfiguredFunctionsForRouterConfig(
        updatedRouterConfig,
        numEdgesWithoutFunctionSpecs,
        numOutboundEdges
      );
    } else if (numEdgesWithoutFunctionSpecs < 0) {
      updatedRouterConfig = removeUnusedFunctionSpecs(
        updatedRouterConfig,
        Math.abs(numEdgesWithoutFunctionSpecs),
        staticFlowchart
      );
    }

    nodeChanges.push({
      type: FlowchartNodeChangeType.REPLACE,
      id: routerNode.id,
      data: produce(routerNode, (draftNode) => {
        draftNode.routerConfig = updatedRouterConfig;
      }),
    });

    // Clear labels on remaining outbound edges no longer referenced by any function spec
    if (changeType === FlowchartEdgeChangeType.REMOVE) {
      const referencedEdgeIds = new Set(
        [...updatedRouterConfig.functionSpecs, updatedRouterConfig.defaultFunctionSpec]
          .filter((spec) => spec?.edgeConfigReference != null)
          .map((spec) => spec!.edgeConfigReference!.clientId)
      );
      for (const edge of outboundEdges) {
        if (edge.label != null && !referencedEdgeIds.has(edge.id)) {
          edgeChanges.push({
            type: FlowchartEdgeChangeType.LABEL,
            id: edge.id,
            label: undefined,
          });
        }
      }
    }
  }

  return {nodeChanges, edgeChanges};
}
